import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { congesApi, type LectureCompteur, type LecturePlafond } from '../../services/conges-api';
import { periodeFr } from '../../lib/periode-conges';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import {
  CarteCompteur,
  ChronologieCompteurs,
  jours,
} from '../../components/conges/Compteurs';

/**
 * « Mes congés » — les soldes du salarié connecté (R-CPT-11).
 *
 * TROIS LECTURES par compteur, et la mise en avant va à la TROISIÈME :
 *   ① le solde courant ;
 *   ② les jours en attente de validation ;
 *   ③ le solde prévisionnel = ① − ②.
 *
 * C'est le prévisionnel qui répond à la question réelle — « je peux encore
 * poser combien ? ». Mettre ① en avant fait raisonner sur le passé et conduit à
 * poser des jours déjà engagés. Airtable affichait les trois ; les salariés y
 * tiennent, et le perdre serait vécu comme une régression.
 */

function CartePlafond({ p }: { p: LecturePlafond }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-sm font-medium text-slate-600">{p.libelle}</p>
      <p className="mt-2 text-3xl font-semibold text-slate-900">
        {jours(Math.max(0, p.restant))}
        <span className="ml-1 text-base font-normal text-slate-500">j</span>
      </p>
      <p className="text-xs text-slate-500">
        sur {jours(p.plafond)} j{' '}
        {p.plafond_periode === 'mois'
          ? 'ce mois-ci'
          : p.plafond_periode === 'stage'
            ? 'depuis le début du stage'
            : 'par an'}
      </p>
      {p.non_remunere && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900">
          Ces congés ne sont pas rémunérés.
        </p>
      )}
      <dl className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-slate-500">
            {p.plafond_periode === 'mois'
              ? 'Pris ce mois-ci'
              : p.plafond_periode === 'stage'
                ? 'Pris depuis le début'
                : 'Pris cette année'}
          </dt>
          <dd className="tabular-nums text-slate-700">{jours(p.pris)} j</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">En attente de validation</dt>
          <dd className={`tabular-nums ${p.en_attente > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
            {p.en_attente > 0 ? `${jours(p.en_attente)} j` : '-'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

const LIBELLE_STATUT: Record<string, string> = {
  brouillon: 'Brouillon',
  attente_manager: 'En attente du manager',
  attente_rh: 'En attente de la RH',
  approuvee: 'Approuvée',
  refusee: 'Refusée',
  annulee: 'Annulée',
};

const COULEUR_STATUT: Record<string, string> = {
  brouillon: 'bg-slate-100 text-slate-700',
  attente_manager: 'bg-amber-100 text-amber-800',
  attente_rh: 'bg-amber-100 text-amber-800',
  approuvee: 'bg-emerald-100 text-emerald-800',
  refusee: 'bg-red-100 text-red-800',
  annulee: 'bg-slate-100 text-slate-500',
};



export function MesCongesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'mes-conges'],
    queryFn: () => congesApi.mesConges(),
  });

  if (isLoading) return <p className="p-6 text-slate-500">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;
  if (!data) return null;

  const enAttente = data.absences.filter((a) => a.statut.startsWith('attente_'));

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Mes congés</h1>
          <p className="text-sm text-slate-500">
            {data.salarie.prenom} {data.salarie.nom}
          </p>
        </div>
        {/* Le geste principal de l'écran, atteignable sans chercher. Il ouvre
            le FORMULAIRE : le calendrier est l'autre porte d'entrée, celle de
            qui part de ses dates plutôt que de son besoin. */}
        <Link
          to="/conges/poser"
          className="min-h-11 rounded-lg bg-nuit px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-nuit/90"
        >
          Poser une absence
        </Link>
      </header>

      {data.compteurs.length === 0 && data.plafonds.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-slate-600">
          Aucun compteur n’est ouvert sur votre fiche. Si cela vous semble anormal, signalez-le à la RH.
        </p>
      ) : (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.compteurs.map((c) => (
            <CarteCompteur key={c.cle} c={c} />
          ))}
          {data.plafonds.map((p) => (
            <CartePlafond key={p.type_id} p={p} />
          ))}
        </section>
      )}

      {enAttente.length > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {enAttente.length === 1
            ? 'Une demande attend une validation : elle est déjà retirée du prévisionnel.'
            : `${enAttente.length} demandes attendent une validation : elles sont déjà retirées du prévisionnel.`}
        </p>
      )}

      <section>
        <h2 className="mb-3 font-[Sora] text-lg font-semibold text-[#384872]">Mes absences</h2>
        {data.absences.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-white p-4 text-slate-500">
            Aucune absence enregistrée.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.absences.map((a) => (
              <li
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3"
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">{a.type_libelle}</p>
                  <p className="text-sm text-slate-500">
                    {periodeFr(a)}
                    {a.description ? ` (${a.description})` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums text-sm text-slate-600">{jours(a.nb_jours)} j</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      COULEUR_STATUT[a.statut] ?? 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {LIBELLE_STATUT[a.statut] ?? a.statut}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-3 font-[Sora] text-lg font-semibold text-[#384872]">
          Le détail de mes compteurs
        </h2>
        {/* La chronologie est ce qui permet à un salarié de comprendre POURQUOI
            son solde vaut ce qu'il vaut : et de le contester s'il se trompe. */}
        <ChronologieCompteurs mouvements={data.mouvements} />
      </section>
    </div>
  );
}

